/* ============================================================
   OUAFY — Sélecteur d'offres / bundles (thème Horizon)
   ------------------------------------------------------------
   Rôle :
   1. Recalculer les prix des offres quand la variante change.
   2. Quand un pack est sélectionné, intercepter "Ajouter au panier"
      (bouton principal ET barre sticky d'Horizon) et ajouter toutes
      les lignes d'un coup via Shopify.actions.updateCart.
      Fallback si l'API n'existe pas : POST /cart/add.js puis
      redirection vers la page panier.

   La remise du pack n'est PAS appliquée ici. C'est la réduction
   automatique créée dans l'admin qui s'en charge : le thème affiche,
   Shopify applique.

   Tout est en délégation d'événements sur `document`, donc le code
   survit au re-rendu de la section produit par Horizon (changement
   de variante, éditeur de thème).
   ============================================================ */
(() => {
  if (window.OuafyOffers) return;

  const SEL = {
    root: '[data-oy-offers]',
    offer: '[data-oy-offer]',
    input: '.oy-offer__input',
    variants: '[data-oy-variants]',
    savings: '[data-oy-savings]',
    savingsAmount: '[data-oy-savings-amount]',
    error: '[data-oy-error]',
    price: '[data-oy-offer-price]',
    compare: '[data-oy-offer-compare]',
    offerSavings: '[data-oy-offer-savings]',
    stickyButton: '.sticky-add-to-cart__button',
  };

  /* ---------- utilitaires ---------- */

  // Reproduit shop.money_format côté client (cents → "129,90 €")
  function formatMoney(cents, format) {
    const fmt = format || '{{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const n = Number(cents) / 100;
    const fix = (value, decimals, thousands, decimal) => {
      const parts = Math.abs(value).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (value < 0 ? '-' : '') + parts.join(decimal);
    };
    let out;
    switch (key) {
      case 'amount_no_decimals': out = fix(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = fix(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fix(n, 0, '.', ','); break;
      case 'amount_with_space_separator': out = fix(n, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = fix(n, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': out = fix(n, 2, "'", '.'); break;
      default: out = fix(n, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  const safeJSON = (text, fallback) => {
    try { return JSON.parse(text); } catch (e) { return fallback; }
  };

  const parseVariants = (root) => safeJSON((root.querySelector(SEL.variants) || {}).textContent, {});

  // Variante actuellement sélectionnée du produit principal
  function getMainVariantId(root, form) {
    const scopes = [form, root.closest('.shopify-section'), document].filter(Boolean);
    for (const scope of scopes) {
      const input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return input.value;
    }
    const sticky = document.querySelector('sticky-add-to-cart[data-current-variant-id]');
    if (sticky && sticky.dataset.currentVariantId) return sticky.dataset.currentVariantId;
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    return fromUrl || root.dataset.variantId;
  }

  const selectedOffer = (root) => {
    const input = root.querySelector(`${SEL.offer} ${SEL.input}:checked`);
    return input ? input.closest(SEL.offer) : null;
  };

  /* ---------- affichage ---------- */

  function refresh(root) {
    const variants = parseVariants(root);
    const id = getMainVariantId(root);
    const variant = variants[id] || variants[root.dataset.variantId] || Object.values(variants)[0];
    if (!variant) return;
    const fmt = root.dataset.moneyFormat;

    root.querySelectorAll(SEL.offer).forEach((offer) => {
      const qty = Number(offer.dataset.qtyMain) || 1;
      const extraPrice = Number(offer.dataset.extraPrice) || 0;
      // Si data-extra-compare manque (ancienne version du bloc) ou vaut 0,
      // on retombe sur le prix : le barré ne peut jamais passer sous le réel.
      const extraCompare = Math.max(Number(offer.dataset.extraCompare) || 0, extraPrice);
      const discount = Number(offer.dataset.discount) || 0;
      const unit = Number(variant.p) || 0;
      const unitCompare = Math.max(Number(variant.c) || 0, unit);
      const total = unit * qty + extraPrice - discount;
      const compare = unitCompare * qty + extraCompare;
      // Avec ces garde-fous, l'économie vaut au minimum la remise du pack.
      const savings = Math.max(0, compare - total);
      offer.dataset.total = total;
      offer.dataset.savings = savings;

      const priceEl = offer.querySelector(SEL.price);
      if (priceEl) priceEl.textContent = formatMoney(total, fmt);

      const compareEl = offer.querySelector(SEL.compare);
      if (compareEl) {
        compareEl.textContent = formatMoney(compare, fmt);
        compareEl.hidden = compare <= total;
      }

      const savingsEl = offer.querySelector(SEL.offerSavings);
      if (savingsEl) {
        savingsEl.hidden = savings <= 0;
        const template = savingsEl.dataset.template || '[montant]';
        savingsEl.textContent = template.replace('[montant]', formatMoney(savings, fmt));
      }

      offer.classList.toggle('is-selected', !!offer.querySelector(`${SEL.input}:checked`));
            // Barre les prix du détail uniquement si l'offre fait gagner quelque chose
      offer.classList.toggle('oy-offer--saving', savings > 0);
    });

    const banner = root.querySelector(SEL.savings);
    if (banner) {
      const offer = selectedOffer(root);
      const savings = offer ? Number(offer.dataset.savings) || 0 : 0;
      banner.hidden = savings <= 0;
      const amountEl = banner.querySelector(SEL.savingsAmount);
      if (amountEl) amountEl.textContent = formatMoney(savings, fmt);
    }
  }

  function init() {
    document.querySelectorAll(SEL.root).forEach((root) => {
      if (!root.querySelector(`${SEL.input}:checked`)) {
        const first = root.querySelector(`${SEL.input}:not(:disabled)`);
        if (first) first.checked = true;
      }
      refresh(root);
    });
  }
  const refreshAll = () => document.querySelectorAll(SEL.root).forEach(refresh);

  /* ---------- erreurs / état ---------- */

  function showError(root, message) {
    const el = root.querySelector(SEL.error);
    if (!el) return;
    el.textContent = message || root.dataset.errorText || 'Impossible d’ajouter le pack au panier.';
    el.hidden = false;
  }
  function hideError(root) {
    const el = root.querySelector(SEL.error);
    if (el) el.hidden = true;
  }
  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  /* ---------- ajout du pack au panier ---------- */

  async function addBundle(root, button, form, offer, extra, qtyMain) {
    const mainId = getMainVariantId(root, form);
    const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    const multiplier = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

    const lines = [{ merchandiseId: String(mainId), quantity: qtyMain * multiplier }].concat(
      extra.map((item) => ({
        merchandiseId: String(item.id),
        quantity: (Number(item.quantity) || 1) * multiplier,
      }))
    );

    setBusy(button, true);
    hideError(root);
    try {
      const actions = window.Shopify && window.Shopify.actions;
      if (actions && typeof actions.updateCart === 'function') {
        // API standard Shopify : Horizon rafraîchit son drawer tout seul
        const result = await actions.updateCart({ lines }, { event: { context: 'product' } });
        if (result && result.userErrors && result.userErrors.length) {
          throw new Error(result.userErrors[0].message);
        }
        if (typeof actions.openCart === 'function') await actions.openCart();
      } else {
        // Fallback : AJAX API classique + page panier
        const response = await fetch(`${root.dataset.cartAddUrl || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: lines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity })),
          }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Erreur panier');
        }
        window.location.href = root.dataset.cartUrl || '/cart';
      }
    } catch (error) {
      showError(root, error && error.message);
    } finally {
      setBusy(button, false);
    }
  }

  // Décide si le clic / submit doit être détourné vers le pack
  function maybeIntercept(event, button, form) {
    const scope = button.closest('.shopify-section') || document;
    const root = scope.querySelector(SEL.root) || document.querySelector(SEL.root);
    if (!root) return;

    const offer = selectedOffer(root);
    if (!offer || offer.classList.contains('oy-offer--unavailable')) return;

    const extra = safeJSON(offer.dataset.extraItems, []);
    const qtyMain = Number(offer.dataset.qtyMain) || 1;
    if (!extra.length && qtyMain === 1) return; // offre simple → flux natif Horizon

    event.preventDefault();
    event.stopImmediatePropagation();
    addBundle(root, button, form, offer, extra, qtyMain);
  }

  /* ---------- écouteurs (délégation, phase de capture) ---------- */

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button, input[type="submit"]');
      if (!button) return;
      const form = button.closest('form');
      const isFormSubmit =
        !!form &&
        /\/cart\/add/.test(form.getAttribute('action') || '') &&
        (button.type || 'submit') === 'submit';
      const isStickyButton = button.matches(SEL.stickyButton);
      const isAddToCartClass = button.classList.contains('add-to-cart-button');
      if (!isFormSubmit && !isStickyButton && !isAddToCartClass) return;
      maybeIntercept(event, button, form || null);
    },
    true
  );

  // Touche Entrée dans le formulaire produit
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      const button = event.submitter || form.querySelector('[type="submit"]');
      if (!button) return;
      maybeIntercept(event, button, form);
    },
    true
  );

  // Changement d'offre ou de variante
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches && target.matches(SEL.input)) {
      refresh(target.closest(SEL.root));
      return;
    }
    if (target.closest && target.closest('form[action*="/cart/add"], product-form-component, variant-picker')) {
      requestAnimationFrame(refreshAll);
    }
  });
  ['variant:update', 'variant:selected', 'variant:change'].forEach((name) => {
    document.addEventListener(name, () => setTimeout(refreshAll, 60));
  });

  // Éditeur de thème + re-rendu de la section par Horizon
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
  let pending = null;
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches(SEL.root)) || (node.querySelector && node.querySelector(SEL.root))) {
          clearTimeout(pending);
          pending = setTimeout(init, 30);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.OuafyOffers = { refreshAll, formatMoney };
})();
